import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, FormField, Input, PasswordInput } from '@/components';
import toast from 'react-hot-toast';
import { registerWithEmail } from '../../api';
import {
  AUTH_BUTTON_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
} from '../../lib/authStyles';

export function RegisterFormEmail({ onSuccess }) {
  const [email, setEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const { mutate: doRegisterEmail, isPending: isPendingEmail } = useMutation({
    mutationFn: registerWithEmail,
    onSuccess: (data) => {
      if (data?.tokens) {
        localStorage.setItem('access_token', data.tokens.access);
        localStorage.setItem('refresh_token', data.tokens.refresh);
      }
      if (data?.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.user.company_id) {
          localStorage.setItem('company_id', data.user.company_id);
        }
        if (data.user.company_name) {
          localStorage.setItem('company_name', data.user.company_name);
        }
      }
      onSuccess(email);
    },
    onError: (err) => {
      const errorData = err.response?.data;
      if (
        errorData &&
        typeof errorData === 'object' &&
        !Array.isArray(errorData) &&
        !errorData.message &&
        !errorData.detail
      ) {
        const newErrors = {};
        Object.keys(errorData).forEach((key) => {
          const errMsg = Array.isArray(errorData[key])
            ? errorData[key][0]
            : errorData[key];
          if (key === 'non_field_errors') {
            toast.error(errMsg);
          } else {
            newErrors[key] = errMsg;
          }
        });
        setFieldErrors(newErrors);
      } else if (Array.isArray(errorData) && errorData.length > 0) {
        toast.error(errorData[0]);
      } else {
        toast.error(
          errorData?.message ||
            errorData?.detail ||
            'Registration failed. Please try again.',
        );
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !emailPassword) {
      const newErrors = {};
      if (!email) newErrors.email = 'Please enter your email.';
      if (!emailPassword) newErrors.password = 'Please enter your password.';
      setFieldErrors(newErrors);
      return;
    }
    doRegisterEmail({ email, password: emailPassword });
  };

  return (
    <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
      <FormField
        label="Email Address"
        htmlFor="email"
        error={fieldErrors.email}
        labelClassName={AUTH_LABEL_CLASS}
      >
        <Input
          size="lg"
          type="email"
          id="email"
          value={email}
          onChange={(e) => {
            if (fieldErrors.email)
              setFieldErrors({ ...fieldErrors, email: '' });
            setEmail(e.target.value);
          }}
          placeholder="name@enterprise.com"
          error={!!fieldErrors.email}
          className={AUTH_INPUT_CLASS}
          disabled={isPendingEmail}
        />
      </FormField>

      <FormField
        label="Password"
        htmlFor="emailPassword"
        error={fieldErrors.password}
        labelClassName={AUTH_LABEL_CLASS}
      >
        <PasswordInput
          size="lg"
          id="emailPassword"
          value={emailPassword}
          onChange={(e) => {
            if (fieldErrors.password)
              setFieldErrors({ ...fieldErrors, password: '' });
            setEmailPassword(e.target.value);
          }}
          placeholder="********"
          error={!!fieldErrors.password}
          className={AUTH_INPUT_CLASS}
          disabled={isPendingEmail}
        />
      </FormField>

      {/* Email Submit Button */}
      <div className="flex justify-center mt-6">
        <Button
          size="lg"
          type="submit"
          variant="primary"
          className={`${AUTH_BUTTON_CLASS} w-[180px] h-[48px]`}
          disabled={isPendingEmail}
        >
          {isPendingEmail ? (
            <span className="flex items-center justify-center w-full">
              <svg
                className="animate-spin h-5 w-5 text-surface-mint"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </span>
          ) : (
            <>
              <span>Continue</span>
              <span>→</span>
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
